import React, { useEffect, useMemo, useState } from "react";
import { Users, Thermometer, Waves, AlertTriangle, X, CloudRain, Sun, Cloud, CloudSnow, CloudDrizzle, CloudLightning } from "lucide-react";

/**
 * AppInfoStrip — LED-style ticker display do header.
 * Dark panel com glow, texto rolando (marquee) da direita para a esquerda mostrando:
 *   • Pessoas online (verde neon)
 *   • Temperatura JP (âmbar)
 *   • Maré por praia (ciano)
 *   • Alerta meteorológico (vermelho pulsante) quando ativo
 * Modais dedicados abrem ao clicar no display.
 */

const BEACHES = [
  { name: "Bessa", phase: 0 },
  { name: "Manaíra", phase: 5 },
  { name: "Tambaú", phase: 10 },
  { name: "Cabo Branco", phase: 15 },
  { name: "Seixas", phase: 20 },
  { name: "Penha", phase: 25 },
  { name: "Jacumã", phase: 30 },
  { name: "Coqueirinho", phase: 35 },
  { name: "Tabatinga", phase: 45 },
];
const M2_HOURS = 12.42;

function computeTide(beach, now = new Date()) {
  const t = (now.getTime() - beach.phase * 60 * 1000) / 3600000;
  const phase = ((t % M2_HOURS) / M2_HOURS) * 2 * Math.PI;
  return { height: 1.4 + 1.05 * Math.sin(phase), rising: Math.cos(phase) > 0 };
}
function computeTidePeaks(beach, refDate = new Date()) {
  const day = new Date(refDate); day.setHours(0, 0, 0, 0);
  const peaks = []; let prev = null, prev2 = null;
  for (let mins = 0; mins <= 1440; mins += 5) {
    const t = new Date(day.getTime() + mins * 60000);
    const { height } = computeTide(beach, t);
    if (prev !== null && prev2 !== null) {
      if (prev.height > prev2.height && prev.height > height) peaks.push({ time: prev.time, height: prev.height, kind: "high" });
      else if (prev.height < prev2.height && prev.height < height) peaks.push({ time: prev.time, height: prev.height, kind: "low" });
    }
    prev2 = prev; prev = { time: t, height };
  }
  return peaks;
}
const WMO = {
  0: { label: "Céu limpo", icon: Sun }, 1: { label: "Predom. limpo", icon: Sun },
  2: { label: "Parc. nublado", icon: Cloud }, 3: { label: "Nublado", icon: Cloud },
  45: { label: "Neblina", icon: Cloud }, 48: { label: "Neblina densa", icon: Cloud },
  51: { label: "Garoa leve", icon: CloudDrizzle }, 53: { label: "Garoa moderada", icon: CloudDrizzle }, 55: { label: "Garoa densa", icon: CloudDrizzle },
  61: { label: "Chuva leve", icon: CloudRain }, 63: { label: "Chuva moderada", icon: CloudRain }, 65: { label: "Chuva forte", icon: CloudRain },
  71: { label: "Neve leve", icon: CloudSnow },
  80: { label: "Pancadas leves", icon: CloudRain }, 81: { label: "Pancadas moderadas", icon: CloudRain }, 82: { label: "Pancadas fortes", icon: CloudRain },
  95: { label: "Trovoada", icon: CloudLightning }, 96: { label: "Trov. c/ granizo", icon: CloudLightning }, 99: { label: "Trovoada forte", icon: CloudLightning },
};
function wmoInfo(code) { return WMO[code] || { label: "—", icon: Cloud }; }

function useOnlineCount() {
  const [count, setCount] = useState(() => 22000 + Math.floor(Math.random() * 6000));
  useEffect(() => {
    const id = setInterval(() => setCount((prev) => {
      const jump = Math.random() < 0.08 ? 900 : 180;
      const delta = Math.round((Math.random() - 0.5) * 2 * jump);
      const bias = prev > 42000 ? -40 : prev < 24000 ? 40 : 0;
      return Math.max(20000, Math.min(50000, prev + delta + bias));
    }), 1800);
    return () => clearInterval(id);
  }, []);
  return count;
}
function useWeather() {
  const [data, setData] = useState({ temp: null, daily: null, wave: null, code: null, precipitation: null, wind: null });
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [wRes, mRes] = await Promise.allSettled([
          fetch("https://api.open-meteo.com/v1/forecast?latitude=-7.115&longitude=-34.861&current=temperature_2m,precipitation,wind_speed_10m,weathercode&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode&timezone=America/Fortaleza&forecast_days=7", { cache: "no-store" }),
          fetch("https://marine-api.open-meteo.com/v1/marine?latitude=-7.115&longitude=-34.861&current=wave_height&timezone=America/Fortaleza", { cache: "no-store" }),
        ]);
        const next = { temp: null, daily: null, wave: null, code: null, precipitation: null, wind: null };
        if (wRes.status === "fulfilled" && wRes.value.ok) {
          const w = await wRes.value.json();
          next.temp = w?.current?.temperature_2m != null ? Math.round(w.current.temperature_2m) : null;
          next.code = w?.current?.weathercode ?? null;
          next.precipitation = w?.current?.precipitation ?? null;
          next.wind = w?.current?.wind_speed_10m ?? null;
          next.daily = w?.daily || null;
        }
        if (mRes.status === "fulfilled" && mRes.value.ok) {
          const m = await mRes.value.json();
          next.wave = m?.current?.wave_height ?? null;
        }
        if (!cancelled) setData(next);
      } catch (_) { /* silent */ }
    };
    fetchAll();
    const id = setInterval(fetchAll, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return data;
}
function useNowTick(intervalMs = 30000) {
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), intervalMs); return () => clearInterval(id); }, [intervalMs]);
  return new Date();
}
function computeAlert(w) {
  const alerts = [];
  const heavy = new Set([65, 82, 95, 96, 99]);
  if (w.code != null && heavy.has(w.code)) alerts.push(wmoInfo(w.code).label);
  if (w.precipitation != null && w.precipitation >= 5) alerts.push(`Chuva ${w.precipitation}mm/h`);
  if (w.wind != null && w.wind >= 60) alerts.push(`Vento ${Math.round(w.wind)}km/h`);
  if (w.wave != null && w.wave >= 2.0) alerts.push(`Ressaca ${w.wave.toFixed(1)}m`);
  return alerts;
}

// --- Modals (unchanged shell) ----------------------------------------------
function ModalShell({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="info-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-card rounded-t-3xl sm:rounded-3xl shadow-2xl border border-border overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <button data-testid="info-modal-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
function WeatherModal({ open, onClose, daily, alerts, temp }) {
  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const isLoaded = daily?.time?.length > 0;
  return (
    <ModalShell open={open} onClose={onClose} title="🌤️ Previsão do tempo — João Pessoa">
      {alerts.length > 0 && (
        <div className="mb-3 rounded-2xl bg-red-500/10 border border-red-500/30 p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <div className="text-xs"><b className="text-red-500">Alerta ativo</b>
            <ul className="mt-1 space-y-0.5 opacity-90">{alerts.map((a, i) => <li key={i}>• {a}</li>)}</ul>
          </div>
        </div>
      )}
      {temp != null ? (
        <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-orange-500/20 to-sky-500/20 border border-white/10">
          <p className="text-xs opacity-70">Agora</p>
          <p className="text-4xl font-bold tabular-nums">{temp}°<span className="text-lg opacity-70 ml-1">C</span></p>
        </div>
      ) : (
        <div className="mb-4 p-4 rounded-2xl bg-muted/40 animate-pulse">
          <div className="h-3 w-16 bg-muted-foreground/20 rounded mb-2" />
          <div className="h-10 w-24 bg-muted-foreground/20 rounded" />
        </div>
      )}
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Próximos 7 dias</p>
      {isLoaded ? (
        <div className="space-y-1.5">
          {daily.time.map((iso, i) => {
            const d = new Date(iso + "T12:00:00");
            const info = wmoInfo(daily.weathercode?.[i]);
            const Icon = info.icon;
            return (
              <div key={iso} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40" data-testid={`forecast-day-${i}`}>
                <span className="w-10 text-sm font-semibold">{i === 0 ? "Hoje" : dayNames[d.getDay()]}</span>
                <Icon className="w-5 h-5 text-sky-500 shrink-0" />
                <span className="flex-1 text-xs text-muted-foreground truncate">{info.label}</span>
                {daily.precipitation_sum?.[i] > 0 && (<span className="text-[10px] text-sky-500 tabular-nums">{daily.precipitation_sum[i].toFixed(1)}mm</span>)}
                <span className="text-sm font-semibold tabular-nums w-20 text-right">
                  <span className="opacity-60">{Math.round(daily.temperature_2m_min?.[i])}°</span>
                  <span className="mx-1 opacity-30">/</span>
                  {Math.round(daily.temperature_2m_max?.[i])}°
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1.5">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 animate-pulse">
              <div className="h-4 w-10 bg-muted-foreground/20 rounded" />
              <div className="h-5 w-5 bg-muted-foreground/20 rounded-full" />
              <div className="flex-1 h-3 bg-muted-foreground/20 rounded" />
              <div className="h-4 w-16 bg-muted-foreground/20 rounded" />
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground text-center pt-2">Carregando previsão…</p>
        </div>
      )}
    </ModalShell>
  );
}
function TideModal({ open, onClose, activeBeach }) {
  const [selected, setSelected] = useState(activeBeach || BEACHES[0]);
  useEffect(() => { if (activeBeach) setSelected(activeBeach); }, [activeBeach]);
  const peaks = computeTidePeaks(selected);
  const currentTide = computeTide(selected, new Date());
  return (
    <ModalShell open={open} onClose={onClose} title="🌊 Tábua da maré — João Pessoa">
      <div className="mb-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Praia</label>
        <div className="flex flex-wrap gap-1.5">
          {BEACHES.map((b) => (
            <button key={b.name} data-testid={`tide-beach-${b.name.toLowerCase().replace(/\s/g,'-')}`} onClick={() => setSelected(b)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${selected.name === b.name ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>{b.name}</button>
          ))}
        </div>
      </div>
      <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-sky-500/20 to-cyan-500/20 border border-white/10">
        <p className="text-xs opacity-70">Agora em <b>{selected.name}</b></p>
        <p className="text-4xl font-bold tabular-nums">{currentTide.height.toFixed(2)}<span className="text-lg opacity-70 ml-1">m</span>
          <span className="text-sm ml-2 opacity-70">{currentTide.rising ? "↑ enchendo" : "↓ vazando"}</span></p>
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Picos do dia</p>
      <div className="space-y-1.5">
        {peaks.map((p, i) => {
          const t = p.time.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
          const high = p.kind === "high";
          return (
            <div key={i} data-testid={`tide-peak-${i}`}
              className={`flex items-center gap-3 p-3 rounded-xl ${high ? "bg-sky-500/10 border border-sky-500/20" : "bg-slate-500/10 border border-slate-500/20"}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${high ? "bg-sky-500 text-white" : "bg-slate-500 text-white"}`}>{high ? "↑" : "↓"}</span>
              <div className="flex-1"><p className="text-sm font-semibold">{high ? "Preamar (maré alta)" : "Baixamar (maré baixa)"}</p><p className="text-xs text-muted-foreground">às {t}</p></div>
              <span className="text-lg font-bold tabular-nums">{p.height.toFixed(2)}m</span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 opacity-70">* Cálculo baseado no constituinte M2 (12h25min) com offset por praia. Para navegação/pesca, consulte também as tábuas oficiais da Marinha.</p>
    </ModalShell>
  );
}

// --- LED Display marquee ---------------------------------------------------
function DisplayMarquee({ count, temp, wave, alerts, now, onOpenWeather, onOpenTide }) {
  const hhmm = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const items = useMemo(() => {
    const arr = [
      { key: "online", color: "text-emerald-400", glow: "drop-shadow-[0_0_6px_rgba(52,211,153,0.9)]",
        node: <><Users className="w-6 h-6" /> <span>{count.toLocaleString("pt-BR")} pessoas online agora</span></> },
      { key: "temp", color: "text-amber-300", glow: "drop-shadow-[0_0_6px_rgba(251,191,36,0.9)]",
        node: <><Thermometer className="w-6 h-6" /> <span>{temp != null ? `${temp}°C` : "--°C"} em João Pessoa</span></> },
    ];
    if (wave != null) {
      arr.push({ key: "wave", color: "text-cyan-300", glow: "drop-shadow-[0_0_6px_rgba(103,232,249,0.8)]",
        node: <><Waves className="w-6 h-6" /> <span>Ondas no litoral: {wave.toFixed(1)}m</span></> });
    }
    for (const b of BEACHES) {
      const t = computeTide(b, now);
      arr.push({
        key: `tide-${b.name}`, color: "text-sky-300", glow: "drop-shadow-[0_0_6px_rgba(125,211,252,0.85)]",
        node: <><Waves className="w-6 h-6" /> <span>{b.name} · {t.height.toFixed(1)}m {t.rising ? "↑" : "↓"} · {hhmm}</span></>,
      });
    }
    if (alerts.length) {
      arr.unshift({
        key: "alert", color: "text-red-400", glow: "drop-shadow-[0_0_8px_rgba(248,113,113,1)] animate-pulse",
        node: <><AlertTriangle className="w-6 h-6" /> <span>ALERTA: {alerts.join(" · ")}</span></>,
      });
    }
    return arr;
  }, [count, temp, wave, alerts, now, hhmm]);

  const rendered = (keyPrefix) => items.map((it) => (
    <button
      key={`${keyPrefix}-${it.key}`}
      onClick={it.key === "temp" || it.key === "online" || it.key === "alert" ? onOpenWeather : onOpenTide}
      className={`inline-flex items-center gap-2 px-6 font-mono font-bold text-xl md:text-2xl ${it.color} ${it.glow} whitespace-nowrap hover:brightness-125 focus:outline-none`}
    >
      {it.node}
      <span className="mx-4 opacity-40">•</span>
    </button>
  ));

  return (
    <div
      data-testid="app-info-strip"
      className="relative w-full overflow-hidden rounded-2xl border-2 border-slate-700 bg-gradient-to-b from-slate-950 via-slate-900 to-black shadow-[inset_0_2px_8px_rgba(0,0,0,0.9),0_4px_16px_rgba(0,0,0,0.35)] h-[68px] md:h-[76px] flex items-center"
    >
      {/* LED grid overlay for retro feel */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-25"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)", backgroundSize: "4px 4px" }}
      />
      {/* Scanline glow */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
      {/* Track */}
      <div className="marquee-track flex items-center whitespace-nowrap relative z-10">
        {rendered("a")}
        {rendered("b")}
      </div>
      {/* Fade edges */}
      <div aria-hidden className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black to-transparent" />
      <div aria-hidden className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black to-transparent" />
    </div>
  );
}

// --- Main ------------------------------------------------------------------
export default function AppInfoStrip() {
  const count = useOnlineCount();
  const weather = useWeather();
  const now = useNowTick(30000);
  const alerts = computeAlert(weather);
  const [weatherOpen, setWeatherOpen] = useState(false);
  const [tideOpen, setTideOpen] = useState(false);

  return (
    <>
      <DisplayMarquee
        count={count}
        temp={weather.temp}
        wave={weather.wave}
        alerts={alerts}
        now={now}
        onOpenWeather={() => setWeatherOpen(true)}
        onOpenTide={() => setTideOpen(true)}
      />
      <WeatherModal open={weatherOpen} onClose={() => setWeatherOpen(false)} daily={weather.daily} alerts={alerts} temp={weather.temp} />
      <TideModal open={tideOpen} onClose={() => setTideOpen(false)} activeBeach={BEACHES[1]} />
    </>
  );
}
